import CheckoutPayment from './CheckoutPayment';
import UserAddress from '../User/UserAddress';

/*
 * Builders for the JSON merge patch bodies each checkout step sends to
 * PATCH /projects/v2/{projectId}. Kept separate from CheckoutAPI so the exact wire shape of every step
 * is verifiable without a network layer - these payloads are where the fiddly parts of the contract
 * live, and getting one subtly wrong tends to fail silently rather than loudly.
 */

/**
 * Build the order metadata payload.
 *
 * The API wants `{ [label]: { value, adminDisplayOptions } }`, where adminDisplayOptions is the
 * definition's own value round-tripped. It controls where the answer surfaces in admin, so dropping it
 * would quietly change how the order prints.
 *
 * This always returns an object, even an empty one, because ProjectV2Provider#order rejects a project
 * whose metadata is null. A checkout that skipped the contact step - because the storefront defines no
 * metadata at all - would otherwise fail at submit with a required-field error.
 *
 * @param {Checkout} checkout - The checkout to read answers from.
 * @returns {Object} The metadata payload, keyed by option label.
 */

export function buildMetadataPayload (checkout) {
    const payload = {};

    checkout.orderMetadataOptions.forEach(option => {
        const value = checkout.orderMetadata[option.label];

        if (value === null || typeof value === 'undefined') {
            return;
        }

        payload[option.label] = {
            value: `${value}`,
            adminDisplayOptions: option.adminDisplayOptions,
        };
    });

    return payload;
}

/**
 * Build the contact step patch.
 *
 * @param {Checkout} checkout - The checkout to serialize.
 * @returns {Object} The patch body.
 */

export function buildContactPatch (checkout) {
    const patch = {
        fname: checkout.firstName,
        lname: checkout.lastName,
        email: checkout.email,
        phone: checkout.phone,
        metadata: buildMetadataPayload(checkout),
    };

    if (checkout.divisionId) {
        patch.divisionId = checkout.divisionId;
    }

    if (checkout.clientId) {
        patch.clientId = checkout.clientId;
    }

    return patch;
}

/**
 * Build the shipping flags and addresses patch - everything in the shipping step except the service
 * itself, which is saved separately so carrier surcharges are priced first.
 *
 * @param {Checkout} checkout - The checkout to serialize.
 * @returns {Object} The patch body.
 */

export function buildShippingFlagsPatch (checkout) {
    const patch = {
        orderShipmentsBlindShip: Boolean(checkout.isBlindShip),
        shipmentsSaturdayDelivery: Boolean(checkout.isSaturdayDelivery),
        shipmentsSignatureOnDelivery: Boolean(checkout.isSignatureOnDelivery),
    };

    if (checkout.shipToAddress) {
        patch.orderShipmentsShipToAddress = UserAddress.toPrefixedId(checkout.shipToAddress);
    }

    // Ship-from only means anything for a blind shipment.
    if (checkout.isBlindShip && checkout.shipFromAddress) {
        patch.orderShipmentsShipFromAddress = UserAddress.toPrefixedId(checkout.shipFromAddress);
    }

    return patch;
}

/**
 * Build the nested shippingService patch.
 *
 * Mirrors order2Controller_rest.js, which branches on whether the selected service id is numeric. A
 * real rated service sends its id plus isVendorService and the reference numbers. The two synthesized
 * options - flat-rate USPS and the shopper-described service - send a null id instead, with the
 * description carried separately and the USPS flag set from the selection itself.
 *
 * Returns null when nothing is selected, so the caller can omit the key entirely rather than clear a
 * previously saved selection.
 *
 * @param {Checkout} checkout - The checkout to serialize.
 * @returns {?Object} The nested patch value, or null when no service has been selected.
 */

export function buildShippingServicePatch (checkout) {
    const rate = checkout.shippingService;

    if (!rate) {
        return null;
    }

    const patch = {};

    if (rate.isCustomService || rate.isUSPSService) {
        patch.shippingServiceId = null;

        if (rate.isCustomService && checkout.customShippingDescription) {
            patch.customShippingServiceDescription = checkout.customShippingDescription;
        }
    } else {
        patch.shippingServiceId = rate.serviceId;
        patch.isVendorService = rate.isVendorService;
        patch.referenceNumber1 = checkout.referenceNumber1;
        patch.referenceNumber2 = checkout.referenceNumber2;
        patch.fedExReferenceNumber = checkout.fedExReferenceNumber;
    }

    patch.isUSPSService = rate.isUSPSService;

    return patch;
}

/**
 * Build the payment step patch.
 *
 * This carries the payment *type*, and must be sent before the card itself:
 * ProjectV2Provider#updatePaymentInfo reads the type off the project before touching the card, returns
 * 200 while silently dropping the card for a non-card type, and fails with a 500 when the type is still
 * null.
 *
 * @param {Checkout} checkout - The checkout to serialize. Its payment must be set.
 * @returns {Object} The patch body.
 */

export function buildPaymentPatch (checkout) {
    const patch = {
        checkoutPaymentTypeId: checkout.payment.type,
        po: checkout.payment.po,
    };

    if (checkout.billToAddress) {
        patch.billingAddressId = UserAddress.toPrefixedId(checkout.billToAddress);
    }

    /*
     * Sent only when the shopper actually chose an amount, and then always - including zero.
     * A truthiness check here would drop `creditLimit: 0`, which is the one value that takes credit
     * back off: omitting the field leaves ProjectV2Provider#patchProjectWithCreditLimit on its else
     * branch, where the server's own automatic application stands. Leaving the field out when the
     * shopper never touched credit is deliberate for the same reason - it keeps whatever is applied.
     */
    if (checkout.isCreditAppliedSet) {
        patch.creditLimit = checkout.creditApplied;
    }

    return patch;
}

/**
 * Build the card payload for `PUT /projects/v2/{projectId}/creditCard`, or null when this payment type
 * stores no card.
 *
 * @param {Checkout} checkout - The checkout to serialize.
 * @returns {?Object} The payment info payload.
 */

export function buildPaymentInfo (checkout) {
    return CheckoutPayment.toDB(checkout.payment);
}
